import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { arrayUnion, doc, getDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../firebaseConfig";

function resolveApiBaseUrl() {
  return String(
    process.env.REACT_APP_API_BASE_URL ||
      "https://us-central1-houari-boumedienne-schoo-4d4c2.cloudfunctions.net",
  ).replace(/\/+$/, "");
}

export default function SubmitPaymentReceiptPage() {
  const { teacherUid = "", intakeId = "" } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [intake, setIntake] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const receiptEntries = useMemo(() => {
    if (!Array.isArray(intake?.paymentReceipts)) return [];
    return [...intake.paymentReceipts].sort(
      (a, b) => Number(b?.createdAtMs || 0) - Number(a?.createdAtMs || 0),
    );
  }, [intake?.paymentReceipts]);

  useEffect(() => {
    let cancelled = false;
    const loadIntake = async () => {
      try {
        setLoading(true);
        setError("");
        const refDoc = doc(db, "programIntakes", intakeId);
        const snap = await getDoc(refDoc);
        if (!snap.exists()) {
          if (!cancelled) setError("رابط غير صالح. لم يتم العثور على الاستمارة.");
          return;
        }
        const data = snap.data() || {};
        if (String(data.teacherUid || "") !== String(teacherUid || "")) {
          if (!cancelled) setError("هذا الرابط لا يطابق بيانات الاستمارة.");
          return;
        }
        if (!cancelled) setIntake({ id: snap.id, ...data });
      } catch (e) {
        if (!cancelled) setError("تعذر تحميل بيانات الاستمارة. حاول لاحقًا.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (intakeId && teacherUid) {
      loadIntake();
    } else {
      setLoading(false);
      setError("رابط غير مكتمل.");
    }

    return () => {
      cancelled = true;
    };
  }, [intakeId, teacherUid]);

  const handleUpload = async () => {
    if (!intake?.id) return;
    if (!selectedFile) {
      setError("يرجى اختيار صورة الوصل أولًا.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError("حجم الملف يجب أن يكون أقل من 10 ميغابايت.");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const safeName = String(selectedFile.name || "receipt.jpg").replace(
        /[^a-zA-Z0-9._-]/g,
        "_",
      );
      const storagePath =
        `programIntakes/${intake.id}/paymentReceipts/` +
        `${Date.now()}-${safeName}`;
      const storageRef = ref(storage, storagePath);
      const snap = await uploadBytes(storageRef, selectedFile);
      const url = await getDownloadURL(snap.ref);

      const receipt = {
        url,
        fileName: selectedFile.name || "receipt",
        createdAtMs: Date.now(),
        createdAtIso: new Date().toISOString(),
      };

      const intakeRef = doc(db, "programIntakes", intake.id);
      await updateDoc(intakeRef, {
        paymentReceipts: arrayUnion(receipt),
      });

      let notificationSent = false;
      try {
        const endpoint =
          `${resolveApiBaseUrl()}/api/program-intakes/notify-receipt-submitted`;
        const notifyResponse = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            intakeId: intake.id,
            teacherUid,
          }),
        });
        const notifyPayload = await notifyResponse.json().catch(() => ({}));
        notificationSent = Boolean(
          notifyResponse.ok && notifyPayload?.ok,
        );
      } catch (notifyError) {
        notificationSent = false;
      }

      const nextSnap = await getDoc(intakeRef);
      const nextData = nextSnap.exists() ? nextSnap.data() || {} : {};
      setIntake({ id: intake.id, ...nextData });
      setSelectedFile(null);
      if (notificationSent) {
        setSuccess("تم إرسال الوصل بنجاح وتم إشعارك وإشعار المدربة بالبريد.");
      } else {
        setSuccess("تم إرسال الوصل بنجاح.");
        setError("تعذر إرسال إشعار البريد حالياً، لكن الوصل تم حفظه بنجاح.");
      }
    } catch (e) {
      setError("تعذر إرسال الوصل. حاول مرة أخرى.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", background: "#f8fafc", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">
        <Card sx={{ borderRadius: 3, border: "1px solid #e2e8f0" }} elevation={0}>
          <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
            <Stack spacing={2}>
              <Typography variant="h5" sx={{ fontWeight: 800, textAlign: "center" }}>
                رفع وصل الدفع
              </Typography>
              <Typography sx={{ color: "#64748b", textAlign: "center" }}>
                يمكنك رفع عدة وصولات بنفس الرابط عند الحاجة.
              </Typography>

              {loading ? (
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography>جارٍ التحميل...</Typography>
                </Stack>
              ) : null}

              {!loading && error ? <Alert severity="error">{error}</Alert> : null}
              {success ? <Alert severity="success">{success}</Alert> : null}

              {!loading && !error && intake ? (
                <>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                        {intake.selectedServiceName || intake.selectedPlan || "الخدمة"}
                      </Typography>
                      <Typography sx={{ color: "#475569" }}>
                        {intake.fullName || intake.leadEmail || "-"}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Button variant="contained" component="label" disabled={submitting}>
                    اختر صورة الوصل
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    />
                  </Button>

                  {selectedFile ? (
                    <Typography sx={{ color: "#334155", fontWeight: 600 }}>
                      {selectedFile.name}
                    </Typography>
                  ) : null}

                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={submitting || !selectedFile}
                    sx={{
                      backgroundColor: "#16a34a",
                      "&:hover": { backgroundColor: "#15803d" },
                    }}
                  >
                    {submitting ? "جاري الإرسال..." : "إرسال الوصل"}
                  </Button>

                  {receiptEntries.length > 0 ? (
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
                          الوصولات المرسلة
                        </Typography>
                        <Stack spacing={1}>
                          {receiptEntries.map((item, idx) => (
                            <Button
                              key={`${item.url}-${idx}`}
                              variant="text"
                              href={item.url}
                              target="_blank"
                              rel="noreferrer"
                              sx={{ justifyContent: "flex-start", textTransform: "none" }}
                            >
                              {item.fileName || `Receipt ${idx + 1}`}
                            </Button>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  ) : null}
                </>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
